import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PopUp, Button } from 'geniebook-ui/dist';
import { useQueryClient } from '@tanstack/react-query';
import { doLogout } from '@utils/logout';
const API_BASE_URL = process.env.API_BASE_URL;
const STUDENT_API_KEY = process.env.STUDENT_API_KEY;
const COOKIE_NAME = 'gbadmin_token';

const config = {
  baseURL: API_BASE_URL,
  headers: {
    'api-key': STUDENT_API_KEY,
    UID: Cookies.get('uid') || null,
  },
  'Content-Type': 'multipart/form-data',
};

const AUTH_API = axios.create(config);

let token: string | null = Cookies.get(COOKIE_NAME) ?? null;

const refreshToken = async (): Promise<string | null> => {
  try {
    const response = await AUTH_API.get('/user/refresh_token/json');
    const responseData = response.data;
    if (!responseData.data) {
      return null;
    }
    const newToken = responseData.data?.access_token;
    const expiryTimestamp = responseData.data?.expired_at || 0;
    const expiryDate = new Date(expiryTimestamp * 1000);

    const domain = window.location.hostname.replace(/(.*\.)?([^.]*\..*)/, '$2');
    Cookies.set(COOKIE_NAME, newToken || '', {
      expires: expiryDate,
      path: '/',
      domain: domain,
    });

    token = newToken;
    return newToken;
  } catch (error) {
    console.error('Error fetching new token:', error);
    throw new Error('Failed to refresh token');
  }
};

async function sendRequest<Type>(config: AxiosRequestConfig): Promise<Type> {
  if (!token) {
    console.log('Cookie not found. Authenticating...');
    await refreshToken();
  }
  const response = await AUTH_API.request<Type>(config);
  return response.data;
}

type props = {
  children: React.ReactNode;
};

const AxiosInterceptor = ({ children }: props) => {
  const [showError, setShowError] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const Logout = () => {
    setShowError(false);
    doLogout(navigate, queryClient);
  };

  const authenticationPopup = {
    buttons: (
      <>
        <Button className="min-w-full w-full" onClick={Logout}>
          Login
        </Button>
      </>
    ),
    show: true,
    msg: `Don't worry though, we've saved all the latest changes you made to your work. Simply login to Geniebook to continue learning!`,
    on_close: Logout,
    title: 'Session has expired',
  };

  useEffect(() => {
    const resInterceptor = (response: AxiosResponse) => {
      const res = response.data;
      if (res.error_code === '300-06') {
        setShowError(true);
      }
      return response;
    };
    const errInterceptor = (error: AxiosError) => {
      return Promise.reject(error);
    };

    const interceptor = AUTH_API.interceptors.response.use(
      resInterceptor,
      errInterceptor,
    );
    return () => AUTH_API.interceptors.response.eject(interceptor);
  }, []);

  return (
    <>
      {showError ? (
        <PopUp
          onClose={authenticationPopup.on_close}
          buttonGroup={authenticationPopup.buttons}
          description={authenticationPopup.msg}
          title={authenticationPopup.title}
          disableBodyScroll={true}
        />
      ) : null}
      {children}
    </>
  );
};

export default AUTH_API;
export { sendRequest, AxiosInterceptor };
